/**
 * TranslationTool Component
 * Allows creating translations of existing games in different languages
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  Translate as TranslateIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { gamesAPI } from '../../services/api';
import { LANGUAGES } from '../../utils/constants';
import { StatusBadge } from '../Games/StatusBadge';

export const TranslationTool = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Form state
  const [targetLanguage, setTargetLanguage] = useState('');
  const [sourceLanguage, setSourceLanguage] = useState('');
  const [search, setSearch] = useState('');
  const [selectedGame, setSelectedGame] = useState(null);
  const [error, setError] = useState('');

  // Fetch games with optional filtering
  const { data: gamesData, isLoading } = useQuery({
    queryKey: ['games', { language: sourceLanguage, search, limit: 100 }],
    queryFn: () => gamesAPI.getAll({
      language: sourceLanguage || undefined,
      search: search || undefined,
      limit: 100
    }),
    enabled: true
  });

  // Filter out games that already exist in target language
  const availableGames = useMemo(() => {
    const gamesList = gamesData?.games || [];
    if (!targetLanguage) return gamesList;

    // Get all game codes that already exist in the target language
    const existingInTarget = new Set(
      gamesList
        .filter(g => g.language === targetLanguage)
        .map(g => g.gameCode)
    );

    // Return games that don't exist in target language
    return gamesList.filter(g =>
      g.language !== targetLanguage &&
      !existingInTarget.has(g.gameCode)
    );
  }, [gamesData?.games, targetLanguage]);

  // Create translation mutation
  const createTranslationMutation = useMutation({
    mutationFn: async ({ sourceId, targetLanguage }) => {
      // First get the full game data
      const sourceGame = await gamesAPI.getById(sourceId);

      // Parse gameData if string
      let gameData = sourceGame.gameData;
      if (typeof gameData === 'string') {
        gameData = JSON.parse(gameData);
      }

      // Update the language in gameData
      const translatedGameData = {
        ...gameData,
        language: targetLanguage
      };

      // Create new game with translated language
      const newGame = await gamesAPI.create({
        gameCode: sourceGame.gameCode,
        gameName: sourceGame.gameName,
        language: targetLanguage,
        schemaVersion: sourceGame.schemaVersion,
        category: sourceGame.category,
        isExpansion: sourceGame.isExpansion,
        isVariant: sourceGame.isVariant,
        parentGameCode: sourceGame.parentGameCode,
        originalGameCode: sourceGame.originalGameCode,
        contributorHandle: sourceGame.contributorHandle,
        gameData: translatedGameData,
        status: 'wip',
        versionReleaseNotes: `Translation from ${sourceGame.language} to ${targetLanguage}`
      });

      return newGame;
    },
    onSuccess: (newGame) => {
      queryClient.invalidateQueries(['games']);
      navigate(`/games/${newGame.id}/edit`);
    },
    onError: (err) => {
      setError(err.response?.data?.message || 'Failed to create translation');
    }
  });

  const handleCreateTranslation = () => {
    if (!selectedGame || !targetLanguage) {
      setError('Please select a game and target language');
      return;
    }

    if (selectedGame.language === targetLanguage) {
      setError('Source and target languages must be different');
      return;
    }

    setError('');
    createTranslationMutation.mutate({
      sourceId: selectedGame.id,
      targetLanguage
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TranslateIcon /> Translation Tool
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Create a translation of an existing game in a different language. The new game will be
        created as a Work in Progress with the same game code but different language.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Step 1: Select Target Language</Typography>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Target Language</InputLabel>
          <Select
            value={targetLanguage}
            label="Target Language"
            onChange={(e) => setTargetLanguage(e.target.value)}
          >
            {LANGUAGES.map(lang => (
              <MenuItem key={lang.code} value={lang.code}>
                {lang.name} ({lang.code})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Step 2: Find Source Game</Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Source Language</InputLabel>
            <Select
              value={sourceLanguage}
              label="Source Language"
              onChange={(e) => setSourceLanguage(e.target.value)}
            >
              <MenuItem value="">All Languages</MenuItem>
              {LANGUAGES.filter(l => l.code !== targetLanguage).map(lang => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.name} ({lang.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Search games"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ flex: 1 }}
          />
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Game Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Language</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availableGames.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {targetLanguage
                        ? 'No games available for translation to this language'
                        : 'No games found'}
                    </TableCell>
                  </TableRow>
                ) : (
                  availableGames.map(game => (
                    <TableRow
                      key={game.id}
                      selected={selectedGame?.id === game.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setSelectedGame(game)}
                    >
                      <TableCell>{game.gameCode}</TableCell>
                      <TableCell>{game.gameName}</TableCell>
                      <TableCell>
                        <Chip
                          label={game.language.toUpperCase()}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={game.status} />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant={selectedGame?.id === game.id ? 'contained' : 'outlined'}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedGame(game);
                          }}
                        >
                          {selectedGame?.id === game.id ? 'Selected' : 'Select'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {selectedGame && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'action.hover' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Step 3: Confirm Translation</Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography>
              <strong>Source:</strong> {selectedGame.gameName} ({selectedGame.language.toUpperCase()})
            </Typography>
            <TranslateIcon color="primary" />
            <Typography>
              <strong>Target:</strong> {targetLanguage ? LANGUAGES.find(l => l.code === targetLanguage)?.name : 'Select a language'}
              {targetLanguage && ` (${targetLanguage.toUpperCase()})`}
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            A new game will be created with:
            <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
              <li>Same game code: <strong>{selectedGame.gameCode}</strong></li>
              <li>New language: <strong>{targetLanguage?.toUpperCase() || '?'}</strong></li>
              <li>Status: <strong>Work in Progress</strong></li>
            </ul>
            You will be redirected to the editor to translate the text content.
          </Alert>

          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<TranslateIcon />}
            onClick={handleCreateTranslation}
            disabled={!targetLanguage || createTranslationMutation.isLoading}
          >
            {createTranslationMutation.isLoading ? 'Creating...' : 'Create Translation'}
          </Button>
        </Paper>
      )}
    </Box>
  );
};
